import { Kicker } from '@developerz/ui';
import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AgentActivity } from '../lib/agent-activity';
import type { ModeSaves } from '../lib/agent-mode-save';
import type { ModelSaves } from '../lib/agent-model-save';
import type { AgentModeView } from '../lib/agent-modes';
import {
  AGENT_CARD_ORDER,
  AGENT_ROUTE_PURPOSE,
  activityFor,
  CODER_TASK_TAG_PURPOSES,
  type CoderTaskTagPurpose,
  modeKeyForAgent,
  orgRouteFor,
  pinTriggerValue,
  repoOverrideCount,
} from '../lib/agents';
import {
  AGENT_PREVIEW_PURPOSE,
  type AgentsPreviewWire,
  previewEntryFor,
} from '../lib/agents-preview';
import type { KeyRow } from '../lib/keys';
import type { RouteWire } from '../lib/llmRoutes';
import { orgHref } from '../lib/org';
import type { RepoRouteSaves } from '../lib/repo-route-save';
import { AgentCard } from './AgentCard';
import { AgentRepoOverridesSection } from './AgentRepoOverridesSection';
import { AgentRouteAdvancedFields } from './AgentRouteAdvancedFields';
import { AgentGridFrame, DefaultCardFrame } from './AgentsFrames';
import { AgentsStatusStrip } from './AgentsStatusStrip';
import { ChangedByChip } from './ChangedByChip';
import { ModelPicker } from './ModelPicker';
import { ModelSaveNotice } from './ModelSaveNotice';

// The Agents page's presenter (#5209, epic #5200): the pool strip, the Default
// card, and the 2-col grid of the ten pin-backed cards (`AGENT_CARD_ORDER`), eight
// of them naming a preview purpose (`AGENT_PREVIEW_PURPOSE`; of those, the
// Planner's `plan` and the Triage's `triage` have no row in the preview read
// today, so those two cards carry no preview line). Container
// (`../routes/Agents.tsx`) owns every read and the save controller (#5210); this
// file only folds and paints, handing each card its purpose's route and the keys
// its `ModelPicker` offers.
//
// The Default card (#5445) also carries the SAME shared Advanced fields
// (`AgentRouteAdvancedFields`, #5436) and Per-repo overrides section
// (`AgentRepoOverridesSection`) every other card's drawer mounts — Default has
// no drawer of its own (it sits above `AGENT_CARD_ORDER`'s grid, not in it), so
// both are mounted directly in the card here, over the ORG `default` route
// (`orgRouteFor`) and its repo-scope rows (`repoRoutesFor`), through the SAME
// `ModelSaves`/`RepoRouteSaves` the picker and drawer already write through.
//
// `routes`/`hasPersonalKeys` are the primary, gating read (the container's
// `QueryBoundary`); `keys`/`activity`/`preview` each degrade on their own
// (Card anatomy: preview failure -> "Couldn't check right now", activity
// failure -> "—") — a stall in any one of them never blanks the whole page.

export type AgentsViewProps = {
  readonly routes: readonly RouteWire[];
  readonly hasPersonalKeys: boolean;
  readonly keys: { readonly data: readonly KeyRow[] | undefined; readonly isError: boolean };
  readonly activity: { readonly data: AgentActivity | undefined; readonly isError: boolean };
  readonly preview: { readonly data: AgentsPreviewWire | undefined; readonly isError: boolean };
  /** The caller may pin (`write:billing`, mirrored from the role; the API arbitrates). */
  readonly canEdit: boolean;
  readonly saves: ModelSaves;

  // #5211: the mode switch + the Advanced drawer.
  /** The org's resolved agent modes, undefined while unread (loading, or a 403
   * from a caller without `write:prompts` — the query still fires; the server
   * remains the enforcer, AC8). */
  readonly modes: readonly AgentModeView[] | undefined;
  readonly canEditModes: boolean;
  readonly modeSaves: ModeSaves;
  readonly repoNames: ReadonlyMap<string, string>;
  readonly repoSaves: RepoRouteSaves;
  readonly token: string | null;
  readonly onKeyRenamed: () => void;
  /** A fixed clock (epoch ms) for the status strip's countdown; a test's seam. Unset,
   * the strip reads the real clock. */
  readonly now?: number;
};

/** This purpose's REPO-scope routes, for the drawer's Per-repo section. */
const repoRoutesFor = (routes: readonly RouteWire[], purpose: string): readonly RouteWire[] =>
  routes.filter((r) => r.scope === 'repo' && r.purpose === purpose);

/** The honesty line(s) a card shows under its pill — pure over the routes and
 * preview data `AgentsView` already holds, so it needs no closure over `props`. */
const honestyFor = (
  routes: readonly RouteWire[],
  previewData: AgentsPreviewWire | undefined,
  id: (typeof AGENT_CARD_ORDER)[number],
): string[] => {
  if (id === 'coder') {
    const line = [t('agents.honesty.coder')];
    const e = previewEntryFor(previewData?.purposes, 'coder');
    const pinned = pinTriggerValue(routes, AGENT_ROUTE_PURPOSE.coder) !== null;
    if (pinned && e?.overrides_templates != null && e.overrides_templates > 0) {
      line.push(t('agents.honesty.coder_pinned', { n: e.overrides_templates }));
    }
    // Unpinned, triage judges every ask on this card's route (#6388) — say so here.
    if (pinTriggerValue(routes, AGENT_ROUTE_PURPOSE.triage) === null) {
      line.push(t('agents.honesty.coder_judges'));
    }
    return line;
  }
  if (id === 'devops') return [t('agents.honesty.devops')];
  if (id === 'scout') return [t('agents.honesty.scout')];
  // Unpinned, the judge runs on the Coder's model: `triageRoute` reads the org
  // `triage` row first and falls back to the org `general` row (the Coder's) when the
  // account pinned none — the triage PIN is its own (#6388).
  if (id === 'triage') {
    return pinTriggerValue(routes, AGENT_ROUTE_PURPOSE.triage) === null
      ? [t('agents.honesty.triage_unpinned')]
      : [];
  }
  // Unpinned, the screen runs on the fast-tier model — never the `default` row (#6938).
  if (id === 'screen') {
    return pinTriggerValue(routes, AGENT_ROUTE_PURPOSE.screen) === null
      ? [t('agents.honesty.screen_unpinned')]
      : [];
  }
  if (id === 'escalation') {
    return pinTriggerValue(routes, AGENT_ROUTE_PURPOSE.escalation) === null
      ? [t('agents.honesty.escalation_unpinned')]
      : [];
  }
  return [];
};

export function AgentsView(props: AgentsViewProps): JSX.Element {
  const defaultRefusal = () => props.saves.refusal(AGENT_ROUTE_PURPOSE.default);

  return (
    <>
      <AgentsStatusStrip keys={props.keys} activity={props.activity} now={props.now} />

      <DefaultCardFrame
        head={
          <>
            <Kicker as="p">{t('agents.default.kicker')}</Kicker>
            <h2 class="card__title">{t('agents.default.title')}</h2>
            <p class="card__desc">{t('agents.job.default')}</p>
          </>
        }
        fields={
          <>
            <ModelPicker
              agentName={t('agents.name.default')}
              purpose={AGENT_ROUTE_PURPOSE.default}
              route={orgRouteFor(props.routes, AGENT_ROUTE_PURPOSE.default)}
              pinValue={pinTriggerValue(props.routes, AGENT_ROUTE_PURPOSE.default)}
              keys={props.keys}
              canEdit={props.canEdit && defaultRefusal()?.refusal.kind !== 'locked'}
              busy={props.saves.isBusy(AGENT_ROUTE_PURPOSE.default)}
              onChoose={(change) => void props.saves.choose(AGENT_ROUTE_PURPOSE.default, change)}
            />
            <AgentRouteAdvancedFields
              idPrefix="agents-default"
              route={orgRouteFor(props.routes, AGENT_ROUTE_PURPOSE.default)}
              keys={props.keys}
              canEdit={props.canEdit && defaultRefusal()?.refusal.kind !== 'locked'}
              busy={props.saves.isBusy(AGENT_ROUTE_PURPOSE.default)}
              onChange={(change) => void props.saves.choose(AGENT_ROUTE_PURPOSE.default, change)}
              token={props.token}
              onKeyRenamed={props.onKeyRenamed}
            />
            <ModelSaveNotice
              pending={defaultRefusal()}
              busy={props.saves.isBusy(AGENT_ROUTE_PURPOSE.default)}
              onApplyAnyway={() => void props.saves.applyAnyway(AGENT_ROUTE_PURPOSE.default)}
              onApplyReset={() => void props.saves.applyWithoutEffort(AGENT_ROUTE_PURPOSE.default)}
              onDismiss={() => props.saves.dismiss(AGENT_ROUTE_PURPOSE.default)}
            />
          </>
        }
        repo={
          <AgentRepoOverridesSection
            agentName={t('agents.name.default')}
            purpose={AGENT_ROUTE_PURPOSE.default}
            repoRoutes={repoRoutesFor(props.routes, AGENT_ROUTE_PURPOSE.default)}
            repoNames={props.repoNames}
            keys={props.keys}
            canEdit={props.canEdit}
            saves={props.repoSaves}
            modeKey={undefined}
            canEditModes={props.canEditModes}
            token={props.token}
            title={t('agents.default.repo_title')}
            ariaLabel={t('agents.default.repo_aria')}
            onKeyRenamed={props.onKeyRenamed}
            compact
          />
        }
      />

      <AgentGridFrame>
        <For each={AGENT_CARD_ORDER}>
          {(agent) => {
            const purpose = AGENT_ROUTE_PURPOSE[agent];
            const showsPreview = AGENT_PREVIEW_PURPOSE[agent] !== undefined;
            const modeKey = modeKeyForAgent(agent);
            return (
              <AgentCard
                agent={agent}
                purpose={purpose}
                route={orgRouteFor(props.routes, purpose)}
                keys={props.keys}
                canEdit={props.canEdit}
                saves={props.saves}
                pinValue={pinTriggerValue(props.routes, purpose)}
                repoOverrides={repoOverrideCount(props.routes, purpose)}
                activity={activityFor(props.activity.data?.agents, agent)}
                preview={previewEntryFor(props.preview.data?.purposes, agent)}
                previewFailed={props.preview.isError}
                activityFailed={props.activity.isError}
                showsPreview={showsPreview}
                honesty={honestyFor(props.routes, props.preview.data, agent)}
                changedBy={<ChangedByChip agent={agent} token={props.token} />}
                repoRoutes={repoRoutesFor(props.routes, purpose)}
                repoNames={props.repoNames}
                repoSaves={props.repoSaves}
                taskTagRoutes={
                  new Map(
                    CODER_TASK_TAG_PURPOSES.map(
                      (p) => [p, orgRouteFor(props.routes, p)] as const,
                    ).filter(
                      (pair): pair is [CoderTaskTagPurpose, RouteWire] => pair[1] !== undefined,
                    ),
                  )
                }
                taskTagRepoRoutes={
                  new Map(
                    CODER_TASK_TAG_PURPOSES.map(
                      (p) => [p, repoRoutesFor(props.routes, p)] as const,
                    ),
                  )
                }
                modeView={
                  modeKey === undefined ? undefined : props.modes?.find((m) => m.key === modeKey)
                }
                canEditModes={props.canEditModes}
                modeSaves={props.modeSaves}
                token={props.token}
                onKeyRenamed={props.onKeyRenamed}
              />
            );
          }}
        </For>
      </AgentGridFrame>

      <Show when={props.hasPersonalKeys}>
        <p class="agents__footer">
          <A href={orgHref('/account/my-keys')}>{t('agents.footer.my_keys')}</A>{' '}
          <span class="agents__footer-note">{t('agents.footer.note')}</span>
        </p>
      </Show>
    </>
  );
}
